import type {
  Conversation,
  ConversationListFilters,
  ConversationListItem,
  ConversationServiceOptions,
  CreateConversationMessagePayload,
  CreateConversationPayload,
  UpdateConversationMessagePayload,
} from './conversation.types'
import { ConversationServiceError } from './conversation.types'

export class ConversationService {
  private readonly apiUrl: string
  private readonly token: string

  constructor(options: ConversationServiceOptions) {
    this.apiUrl = options.apiUrl
    this.token = options.token
  }

  private async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const query = params ? this.buildQuery(params) : ''
    const res = await fetch(`${url}${query}`, {
      headers: { Authorization: `Bearer ${this.token}` },
    })
    if (!res.ok) throw await this.toError('GET', url, res)
    return res.json() as Promise<T>
  }

  private async post<T>(url: string, body?: unknown): Promise<T> {
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw await this.toError('POST', url, res)
    return res.json() as Promise<T>
  }

  private async toError(method: string, url: string, res: Response): Promise<ConversationServiceError> {
    // An error body isn't guaranteed to be JSON (e.g. a proxy's HTML error page), so a failed parse is ignored.
    const body: unknown = await res.json().catch(() => null)
    const message = body && typeof body === 'object' && 'message' in body ? body.message : undefined
    const apiMessage = Array.isArray(message) ? message.join(', ') : typeof message === 'string' ? message : undefined

    return new ConversationServiceError(`${method} ${url} failed with status ${res.status}`, res.status, apiMessage)
  }

  private buildQuery(params: Record<string, unknown>): string {
    const query = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      query.set(key, String(value))
    })

    const serialized = query.toString()
    return serialized ? `?${serialized}` : ''
  }

  /**
   * Fetch a single conversation with all its messages.
   */
  getConversation(conversationId: string): Promise<Conversation> {
    return this.get<Conversation>(`${this.apiUrl}/conversations/${conversationId}`)
  }

  /**
   * Fetch all conversations for an application.
   */
  getApplicationConversations(
    applicationId: string,
    filters?: ConversationListFilters,
  ): Promise<ConversationListItem[]> {
    return this.get<ConversationListItem[]>(`${this.apiUrl}/applications/${applicationId}/conversations`, {
      ...filters,
    })
  }

  /**
   * Fetch all conversations for a debt.
   */
  getDebtConversations(debtId: string, filters?: ConversationListFilters): Promise<ConversationListItem[]> {
    return this.get<ConversationListItem[]>(`${this.apiUrl}/debts/${debtId}/conversations`, { ...filters })
  }

  /**
   * Fetch conversations across all accessible application and debt targets.
   */
  getConversations(filters?: ConversationListFilters): Promise<ConversationListItem[]> {
    return this.get<ConversationListItem[]>(`${this.apiUrl}/conversations`, { ...filters })
  }

  /**
   * Create a new conversation for an application or debt.
   */
  createConversation(payload: CreateConversationPayload): Promise<Conversation> {
    return this.post<Conversation>(`${this.apiUrl}/conversations`, payload)
  }

  /**
   * Post a new message in an existing conversation.
   */
  createConversationMessage(conversationId: string, payload: CreateConversationMessagePayload): Promise<Conversation> {
    return this.post<Conversation>(`${this.apiUrl}/conversations/${conversationId}/message`, payload)
  }

  /**
   * Update either the content or read state of an existing conversation message.
   */
  updateConversationMessage(
    conversationId: string,
    messageId: string,
    payload: UpdateConversationMessagePayload,
  ): Promise<Conversation> {
    return this.post<Conversation>(`${this.apiUrl}/conversations/${conversationId}/message/${messageId}`, payload)
  }

  /**
   * Soft delete a conversation message.
   */
  deleteConversationMessage(conversationId: string, messageId: string): Promise<Conversation> {
    return this.post<Conversation>(`${this.apiUrl}/conversations/${conversationId}/message/${messageId}/delete`)
  }

  /**
   * Close an existing conversation.
   */
  closeConversation(conversationId: string): Promise<Conversation> {
    return this.post<Conversation>(`${this.apiUrl}/conversations/${conversationId}/close`)
  }

  /**
   * Reopen an existing conversation.
   */
  reopenConversation(conversationId: string): Promise<Conversation> {
    return this.post<Conversation>(`${this.apiUrl}/conversations/${conversationId}/reopen`)
  }
}
